// ============================================================
// PERRY — THE BEB BODY (all four starters)
// Built per the gorgeous-model recipe: NO boxes — anatomy from
// non-uniformly scaled spheres, capsules and cones; ONE painted,
// cached, SEEDED canvas coat per species; textured eye discs
// that glow for free (toneMapped=false, zero lights spent); an
// ink-line contour so primitives read as DRAWN; idle life on
// fixed phases; a rim aura + orbiting wisps for the magic.
//
// ARCHITECTURE LAW (walkable-worlds): the body owns NO state,
// takes NO position props, and NEVER re-renders while moving.
// It reads posRef / facingRef / speedRef inside useFrame and
// writes the group transform imperatively. React hears nothing.
//
// This is the SLICE body — one shared anatomy, four species
// coats + signature silhouettes. Each starter's full dedicated
// gorgeous pass is its own future visuals session (roadmapped),
// same as the Spectral Tiger got.
//
// THE SWING PASS, 2026-07-27 (caP'n: "if it's not Sexy I ain't
// eatin"). Four things were wrong and all four were structural:
//
//   1. THERE WAS NO SWING. The paw LERPED to the guard point at
//      dt*24. A lerp is a slide. No anticipation, no arc, no
//      contact, no follow-through — every principle that makes a
//      parry read as a parry was simply absent.
//   2. THE ARM HAD NO SHOULDER. It anchored at (p.x, 0.34, p.z)
//      — Perry's CENTRELINE, inside his chest — and it was one
//      cylinder whose LENGTH was scaled from 0.12 to past 1.0.
//      A rubber hose growing out of his stomach. This is exactly
//      the thing caP'n named.
//   3. HE HAD THREE PAWS. pawL, pawR and the rig's own paw were
//      all rendered at once whenever a Treasure was equipped.
//   4. THE BODY SAID NOTHING. High guard and low guard moved the
//      same zero muscles. In a game whose entire premise is READ
//      THE BODY, the player's own body was mute.
//
// Now: a real two-bone arm with fixed-length bones and a solved
// elbow, a bezier swing arc driven by the shared `swingEnvelope`,
// hitstop on contact, and a torso / head / feet / tail / off-paw
// that all perform the angle. The Treasure POINTS at the incoming
// fire and the paw stands off one haft-length behind it, which is
// also what fixed the reach problem — the rose is wider than Perry
// is tall, and it always will be, because the guard is allowed to
// be the biggest gesture on the screen.
// ============================================================

import { useRef, useMemo } from "react";
import * as THREE from "three";
import { useFrame, useThree } from "@react-three/fiber";
import { SPECIES, GUARD } from "./perryData";
import { mulberry32, swingEnvelope, bez1 } from "./qteEngine";
import TreasureBody from "./TreasureBody";

// ── The scale contract — everything measures against Perry ───
const UP = new THREE.Vector3(0, 1, 0);

export const BODY_H = 0.92;   // crown of the head
export const BODY_EYE = 0.72; // first-person camera height
export const BODY_R = 0.30;   // footprint

const INK = "#0c0806";

// ── THE ARM — every number here is a dial ────────────────────
// Bone lengths are FIXED. Nothing about this rig ever scales a
// limb; that was the noodle. When the guard is out of reach the
// SHOULDER travels and the torso leans, which is what a body
// actually does and which happens to look far better anyway.
//
// Positions in the U/V block are on the CAMERA PLANE, measured
// from the centre of the guard rose — same basis `roseWorld`
// uses, per the Screen Law. U is camera-right, V is camera-up.
const ARM = {
  UPPER: 0.27,        // shoulder → elbow
  FORE:  0.29,        // elbow → paw
  SH_U:  0.19,        // where the shoulder sits, off the rose centre
  SH_V: -0.12,        //   (world y ≈ 0.44 — the top of the loaf)
  GRIP:  0.34,        // fraction up the haft the paw closes on
  TR_SCALE: 1.34,     // must match the scale TreasureBody is mounted at
  // Three rows now, not two. Everyone chokes up to block low and
  // holds long to block high; the middle row sits between.
  CHOKE_LOW: 0.62,    // a low guard is held SHORT
  CHOKE_MID: 0.82,
  DROP_HIGH:  0.05,   // the top row rides a little up the body
  DROP_MID:   0.00,
  DROP_LOW:  -0.11,   // the bottom row drops the paws (eased from -0.15:
                      //   the rows carry more of their own vertical
                      //   separation now, so the body needn't exaggerate)
  STAND_MIN: 0.18,    // the paw is never on top of the fire
  STAND_MAX: 0.78,    // ...nor so far back the hand stops travelling between angles
  FLOOR_Y: 0.055,     // the paw never goes through the road
  REACH_MIN: 0.17,    // below this the elbow chicken-wings absurdly
  REST_U: 0.36,       // idle carry — LOW and out, deliberately below every
                      //   guard on the rose, so even the first low-right
                      //   parry of a fight is a visible journey
  REST_V: -0.40,
  REST_AU: 0.42,      // idle aim: the Treasure rests canted up and out
  REST_AV: 0.91,
  REST_ROLL: 0.50,
  RETURN_MS: 300,     // how long the walk back to idle carry takes
  ROLL_WHIP: 0.55,    // extra axial twist carried through the swing
  ARC: 0.40,          // how far off the straight line the swing bows
  LOOK: 0.62,         // how much of the angle the head actually turns
  TWIST: 0.30,        // torso twist into the guard
  LEAN: 0.15,         // torso roll into the guard
  RISE_HIGH: 0.055,   // he stands UP to take a high one
  RISE_LOW: -0.075,   // and drops into it to take a low one
};
const ARM_MAX = (ARM.UPPER + ARM.FORE) * 0.985;
const ARM_MIN = Math.abs(ARM.UPPER - ARM.FORE) + 0.06;   // below this the elbow has nowhere to go
const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);

// ── WHERE THE PAW GOES FOR AN ANGLE ──────────────────────────
// Solved on the camera plane, in U/V, so it is camera-relative by
// construction and can never drift out from under the Screen Law.
//
// The load-bearing idea: the guard point is where the TREASURE
// meets the fire, not where the paw does. The paw therefore stands
// off one haft-length BEHIND the angle, along the spoke. This is
// what a parry is — you put the blade on the blow, not your hand —
// and it is also the only reason the geometry closes at all: the
// rose is 1.24 world units wide and Perry is 0.92 tall with a
// 0.56 arm. He cannot reach the corners. The Treasure can.
//
// Writes { u, v, au, av, roll } into `out`. No allocation.
function guardPose(dirs, key, haft, out) {
  if (!key) {
    out.u = ARM.REST_U; out.v = ARM.REST_V;
    out.au = ARM.REST_AU; out.av = ARM.REST_AV; out.roll = ARM.REST_ROLL;
    return out;
  }
  const d = dirs[key];
  const low = d.tier === "low";
  const mid = d.tier === "mid";

  // the tip, on the rose — the same point Sparks bursts at
  const tu = d.ax * GUARD.SPREAD_X;
  const tv = d.ay * GUARD.SPREAD_Y;
  const L = Math.hypot(tu, tv) || 1e-4;
  const su = tu / L, sv = tv / L;            // the spoke, outward

  // how far back down the spoke the paw grips
  const full = (haft + 0.085 - haft * ARM.GRIP) * ARM.TR_SCALE;
  const choke = low ? ARM.CHOKE_LOW : mid ? ARM.CHOKE_MID : 1;
  const back = clamp(full * choke, ARM.STAND_MIN, L * ARM.STAND_MAX);

  let u = tu - su * back;
  let v = tv - sv * back + (low ? ARM.DROP_LOW : mid ? ARM.DROP_MID : ARM.DROP_HIGH);

  // RESTORE THE STANDOFF. On a BRACE the spoke already points
  // straight down and the low-guard drop points straight down too,
  // so the two cancelled and the paw ended up 0.13 from the angle
  // — contact landing a fifth of the way up the haft, i.e. Perry
  // catching thrown fire with his fist. The drop is allowed to
  // change the DIRECTION the guard is held from (that is the whole
  // expressive point of it) but never its LENGTH.
  const gu = u - tu, gv = v - tv;
  const gl = Math.hypot(gu, gv) || 1e-4;
  u = tu + (gu / gl) * back;
  v = tv + (gv / gl) * back;

  // keep it inside the arm's actual working range
  const du = u - ARM.SH_U, dv = v - ARM.SH_V;
  const dist = Math.hypot(du, dv) || 1e-4;
  const use = clamp(dist, ARM.REACH_MIN, ARM_MAX);
  if (use !== dist) {
    u = ARM.SH_U + (du / dist) * use;
    v = ARM.SH_V + (dv / dist) * use;
  }

  out.u = u; out.v = v;
  // AIM AT THE CONTACT POINT, not down the raw spoke. The paw gets
  // dropped for a low guard and clamped for reach, and the spoke
  // knows about neither — aiming along it left the business end up
  // to half a world unit off the angle, which on the long Treasures
  // put the sparks down by his fist. Solving the aim LAST, from
  // wherever the paw actually ended up, makes the blade point at
  // the fire by construction no matter what moved it.
  const fu = tu - u, fv = tv - v;
  const fl = Math.hypot(fu, fv) || 1e-4;
  out.au = fu / fl; out.av = fv / fl;
  out.roll = d.roll;
  return out;
}

// ── Painted coat — one cached 512² canvas per species ────────
const COAT_CACHE = new Map();

const stroke = (g, x, y, len, wd, ang, fill, alpha) => {
  g.save(); g.translate(x, y); g.rotate(ang);
  g.beginPath(); g.moveTo(0, -len / 2);
  g.quadraticCurveTo(wd, -len * 0.1, 0, len / 2);
  g.quadraticCurveTo(-wd, -len * 0.1, 0, -len / 2);
  g.closePath(); g.globalAlpha = alpha; g.fillStyle = fill; g.fill();
  g.restore(); g.globalAlpha = 1;
};

function coatTexture(speciesId) {
  if (COAT_CACHE.has(speciesId)) return COAT_CACHE.get(speciesId);
  const sp = SPECIES[speciesId] || SPECIES.h01;
  const c = document.createElement("canvas");
  c.width = 512; c.height = 512;
  const g = c.getContext("2d");
  const R = mulberry32(speciesId.length * 7919 + 101);

  // Base — a vertical gradient in the card's ground color.
  const grad = g.createLinearGradient(0, 0, 0, 512);
  grad.addColorStop(0, sp.coatHot);
  grad.addColorStop(0.45, sp.coat);
  grad.addColorStop(1, "#0a0806");
  g.fillStyle = grad; g.fillRect(0, 0, 512, 512);

  // Mottle pass — seeded fur/hide grain.
  for (let i = 0; i < 260; i++) {
    const x = R() * 512, y = R() * 512;
    const r = 3 + R() * 9;
    g.globalAlpha = 0.05 + R() * 0.07;
    g.fillStyle = R() > 0.5 ? sp.coatHot : "#000";
    g.beginPath(); g.arc(x, y, r, 0, Math.PI * 2); g.fill();
  }
  g.globalAlpha = 1;

  // Signature marking pass — leaf strokes in the species voice.
  const marks = 16;
  for (let i = 0; i < marks; i++) {
    const x = 40 + R() * 432, y = 60 + R() * 400;
    const len = 40 + R() * 70, wd = 8 + R() * 12;
    const ang = (R() - 0.5) * 1.4;
    stroke(g, x, y, len, wd, ang, sp.glow, 0.10 + R() * 0.12);
    stroke(g, x, y, len * 0.55, wd * 0.5, ang, sp.coatHot, 0.16);
  }

  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace; // without this the palette washes out
  COAT_CACHE.set(speciesId, t);
  return t;
}

// ── Painted eye — 128² radial lamp, cached per species ───────
const EYE_CACHE = new Map();
function eyeTexture(speciesId) {
  if (EYE_CACHE.has(speciesId)) return EYE_CACHE.get(speciesId);
  const sp = SPECIES[speciesId] || SPECIES.h01;
  const c = document.createElement("canvas");
  c.width = 128; c.height = 128;
  const g = c.getContext("2d");
  const grad = g.createRadialGradient(64, 58, 4, 64, 64, 62);
  grad.addColorStop(0, "#ffffff");
  grad.addColorStop(0.28, sp.eye);
  grad.addColorStop(0.75, sp.glow);
  grad.addColorStop(1, "#120a06");
  g.fillStyle = grad; g.fillRect(0, 0, 128, 128);
  // round pupil
  g.fillStyle = "rgba(8,5,4,0.9)";
  g.beginPath(); g.arc(64, 66, 17, 0, Math.PI * 2); g.fill();
  // specular star
  g.fillStyle = "rgba(255,255,255,0.95)";
  g.beginPath(); g.arc(50, 48, 7, 0, Math.PI * 2); g.fill();
  g.beginPath(); g.arc(76, 76, 3.4, 0, Math.PI * 2); g.fill();
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  EYE_CACHE.set(speciesId, t);
  return t;
}

// ── Ink contour helper — the drawn outline ───────────────────
function Ink({ scale = 1.05, children }) {
  return (
    <mesh scale={scale}>
      {children}
      <meshBasicMaterial color={INK} side={THREE.BackSide} />
    </mesh>
  );
}

// ── Species signature silhouettes ────────────────────────────
function SpeciesDetails({ sp, coat }) {
  if (sp.id === "shadow_fox") {
    return (
      <group>
        {/* the kit's ears */}
        {[-0.13, 0.13].map((x, i) => (
          <group key={i} position={[x, 0.34, 0.02]} rotation={[0.15, 0, x > 0 ? -0.28 : 0.28]}>
            <mesh castShadow>
              <coneGeometry args={[0.09, 0.24, 10]} />
              <meshStandardMaterial map={coat} roughness={0.85} />
            </mesh>
            <mesh scale={1.12}>
              <coneGeometry args={[0.09, 0.24, 10]} />
              <meshBasicMaterial color={INK} side={THREE.BackSide} />
            </mesh>
          </group>
        ))}
      </group>
    );
  }
  if (sp.id === "chaos_dragon") {
    return (
      <group>
        {/* storm horns */}
        {[-0.11, 0.11].map((x, i) => (
          <mesh key={i} position={[x, 0.36, 0.04]} rotation={[-0.35, 0, x > 0 ? -0.5 : 0.5]} castShadow>
            <coneGeometry args={[0.05, 0.2, 8]} />
            <meshStandardMaterial color="#e8d8c8" roughness={0.5} emissive={sp.glow} emissiveIntensity={0.12} />
          </mesh>
        ))}
        {/* whelp wings — flattened cones folded at the back */}
        {[-1, 1].map((s, i) => (
          <mesh key={`w${i}`} position={[s * 0.2, 0.02, -0.16]} rotation={[0.5, s * 0.9, s * 0.5]} scale={[1, 0.35, 1]}>
            <coneGeometry args={[0.16, 0.34, 8]} />
            <meshStandardMaterial map={coat} roughness={0.8} side={THREE.DoubleSide} />
          </mesh>
        ))}
      </group>
    );
  }
  if (sp.id === "antdroid") {
    return (
      <group>
        {/* antennae with emissive tips */}
        {[-0.08, 0.08].map((x, i) => (
          <group key={i} position={[x, 0.38, 0.05]} rotation={[-0.4, 0, x > 0 ? -0.25 : 0.25]}>
            <mesh>
              <cylinderGeometry args={[0.012, 0.012, 0.22, 6]} />
              <meshStandardMaterial color="#3a2a18" roughness={0.6} />
            </mesh>
            <mesh position={[0, 0.13, 0]}>
              <sphereGeometry args={[0.035, 10, 10]} />
              <meshBasicMaterial color={sp.eye} toneMapped={false} />
            </mesh>
          </group>
        ))}
      </group>
    );
  }
  // h01 — the Blue Giant's halo crest
  return (
    <mesh position={[0, 0.44, 0]} rotation={[Math.PI / 2.4, 0, 0]}>
      <torusGeometry args={[0.15, 0.014, 8, 28]} />
      <meshBasicMaterial color={sp.glow} toneMapped={false} transparent opacity={0.8} depthWrite={false} />
    </mesh>
  );
}

// ── THE BODY ─────────────────────────────────────────────────
// posRef: {current:THREE.Vector3-like {x,y,z}} · facingRef: rad ·
// speedRef: 0 idle · 1 walk. emberLight: include the carried
// spirit-ember pointLight (counted in the stage's fixed budget).
export default function PerryBody({
  species = "h01",
  posRef, facingRef, speedRef,
  emberLight = true,
  hitRef,     // {current: 0..1} — red flinch amount, decays in stage
  // ── THE GUARD ROSE, 2026-07-26 ──
  // guardRef carries { key, at, grade }. The paw and the Treasure
  // read it in useFrame and snap to that angle. Nothing about the
  // guard ever touches React — a parry must never cost a render.
  treasure, finish, guardRef,
}) {
  const { camera } = useThree();
  const group = useRef();
  const rigRoot = useRef();      // world-positioned, NEVER yawed
  const armRig = useRef();       // OUTSIDE the yaw group — see the guard block
  const upper = useRef();        // shoulder → elbow, FIXED length
  const fore = useRef();         // elbow → paw,   FIXED length
  const deltoid = useRef();      // the shoulder ball. He has shoulders now.
  const elbowBall = useRef();
  const inner = useRef();
  const head = useRef();
  const aura = useRef();
  const ember = useRef();
  const tail = useRef();
  const pawL = useRef();
  const pawR = useRef();
  const footL = useRef();
  const footR = useRef();
  const wisps = useRef([]);
  const phase = useRef(0);
  const yaw = useRef(0);

  const sp = SPECIES[species] || SPECIES.h01;
  const coat = useMemo(() => coatTexture(sp.id), [sp.id]);
  const eye = useMemo(() => eyeTexture(sp.id), [sp.id]);

  const haft = treasure?.shape?.haft ?? 0.5;
  const gripDrop = haft * ARM.GRIP * ARM.TR_SCALE;   // paw sits up the haft

  // ── SCRATCH — allocated once, written in place forever after ──
  // A fight must not produce garbage. Nothing below this line ever
  // calls `new` inside useFrame.
  const S = useMemo(() => ({
    sh: new THREE.Vector3(), hand: new THREE.Vector3(),
    elbow: new THREE.Vector3(), aim: new THREE.Vector3(),
    dir: new THREE.Vector3(), pole: new THREE.Vector3(),
    fwd: new THREE.Vector3(), tmp: new THREE.Vector3(),
    qa: new THREE.Quaternion(), qb: new THREE.Quaternion(),
    env: {},
    // THE FRAME-ONE LATCH, fixed 2026-07-27. `key` starts UNDEFINED,
    // not null. On the very first frame no guard is up, so `key` is
    // null — and `null !== null` is FALSE, so the latch never fired,
    // `to` was never solved, the bezier read `undefined` control
    // points, and S.u went NaN. That NaN went straight into the
    // torso's transform, three.js culled the group, and Perry
    // vanished from the road. Worse, the next real latch copied the
    // NaN into `from`, so it could never heal. `null !== undefined`
    // is TRUE, which makes frame one solve the rest pose properly.
    key: undefined, at: -1, pressAt: -99999, kind: "rest",
    // ...and both poses start FILLED, so there is no window in which
    // a bezier can be handed an undefined endpoint at all.
    from: { u: ARM.REST_U, v: ARM.REST_V, au: ARM.REST_AU, av: ARM.REST_AV, roll: ARM.REST_ROLL },
    to:   { u: ARM.REST_U, v: ARM.REST_V, au: ARM.REST_AU, av: ARM.REST_AV, roll: ARM.REST_ROLL },
    u: ARM.REST_U, v: ARM.REST_V,
    au: ARM.REST_AU, av: ARM.REST_AV, roll: ARM.REST_ROLL,
    cu: ARM.REST_U, cv: ARM.REST_V,      // the bezier's control point
  }), []);

  useFrame((state, dt) => {
    const g = group.current;
    if (!g) return;
    const p = posRef?.current;
    // the yaw group carries the body; the rig root carries the arm
    // and is NEVER rotated, so a world-space guard offset stays a
    // world-space guard offset.
    if (p) {
      if (rigRoot.current) rigRoot.current.position.set(p.x, p.y || 0, p.z);
      g.position.set(0, 0, 0);
    }

    // shortest-arc eased yaw toward facing
    const want = facingRef?.current ?? 0;
    let d = want - yaw.current;
    while (d > Math.PI) d -= Math.PI * 2;
    while (d < -Math.PI) d += Math.PI * 2;
    yaw.current += d * Math.min(1, dt * 10);
    g.rotation.y = yaw.current;

    // walk cycle — phase advances with SPEED, not time
    const speed = speedRef?.current ?? 0;
    phase.current += speed * dt * 9;
    const ph = phase.current;
    const t = state.clock.elapsedTime;
    const walking = speed > 0.05;

    // ══════════════════════════════════════════════════════════
    //  THE SWING
    // ══════════════════════════════════════════════════════════
    // Everything the parry does — paw, blade, torso, head, feet,
    // tail, off-paw — comes out of the four numbers below. They
    // are solved ONCE per frame, here, and then applied. That is
    // the whole reason it now reads as one motion: there is only
    // one motion, and it has one clock.
    const gd = guardRef?.current;
    const key = gd?.key || null;
    const at = gd?.at || 0;
    const tms = performance.now();

    // THE DOUBLE-WRITE TRAP. The director writes guardRef TWICE
    // for a single press — once in the keydown handler, and again
    // inside resolve() a fraction of a millisecond later when the
    // grade lands. A naive `at !== lastAt` re-latches on the very
    // next frame and eats the anticipation whole. Hence the 50 ms
    // floor: close together is one press, far apart is two.
    if (key !== S.key || at - S.at > 50) {
      S.from.u = S.u; S.from.v = S.v;
      S.from.au = S.au; S.from.av = S.av; S.from.roll = S.roll;
      guardPose(GUARD.DIRS, key, haft, S.to);
      S.pressAt = tms;
      S.kind = key ? "guard" : "return";
      // THE CONTROL POINT — thrown outward, away from the centre
      // of the rose, so the paw travels an ARC. This single number
      // is the difference between a swing and a slide, and a slide
      // is what we had.
      const dU = S.to.u - S.from.u, dV = S.to.v - S.from.v;
      const len = Math.hypot(dU, dV) || 1e-4;
      let pu = -dV / len, pv = dU / len;
      const mu = (S.from.u + S.to.u) * 0.5, mv = (S.from.v + S.to.v) * 0.5;
      if (pu * mu + pv * mv < 0) { pu = -pu; pv = -pv; }
      S.cu = mu + pu * len * ARM.ARC;
      S.cv = mv + pv * len * ARM.ARC;
    }
    S.key = key; S.at = at;

    const age = tms - S.pressAt;
    let E;
    if (S.kind === "return") {
      // Relaxing back to the idle carry. No wind-up, no contact —
      // a recovery is not a strike and must not be animated as one.
      const q = clamp(age / ARM.RETURN_MS, 0, 1);
      S.env.reach = q * q * (3 - 2 * q);
      S.env.bow = Math.sin(q * Math.PI) * 0.3;
      S.env.imp = 0; S.env.ring = 0; S.env.broke = 0;
      S.env.hitstop = 0; S.env.power = 0.4; S.env.live = q < 1;
      S.env.phase = "return";
      E = S.env;
    } else {
      E = swingEnvelope(age, gd?.grade || null, S.env);
    }
    const r = E.reach;

    // the paw, on the arc
    S.u = bez1(S.from.u, S.cu, S.to.u, r);
    S.v = bez1(S.from.v, S.cv, S.to.v, r);
    // The blade swings THROUGH the angle, never merely to it — the
    // aim rides the same parameter, so it whips round behind him on
    // the wind-up and carries past on the follow-through.
    const au0 = S.from.au + (S.to.au - S.from.au) * r;
    const av0 = S.from.av + (S.to.av - S.from.av) * r;
    const an = Math.hypot(au0, av0) || 1e-4;
    S.au = au0 / an; S.av = av0 / an;
    S.roll = S.from.roll + (S.to.roll - S.from.roll) * r + (r - 1) * ARM.ROLL_WHIP;

    // BELT AND BRACES, AND THEY ARE NOT OPTIONAL. One NaN reaching
    // S.u propagates into the torso transform and Perry is culled
    // out of the world entirely — no error, no warning, he is just
    // gone. That is a whole playtest lost to two characters. This
    // costs two comparisons a frame and it fully self-heals: it
    // re-solves the pose and rewrites BOTH ends of the curve, so a
    // poisoned `from` cannot carry the fault into the next swing.
    if (!Number.isFinite(S.u) || !Number.isFinite(S.v) || !Number.isFinite(S.roll)) {
      guardPose(GUARD.DIRS, key, haft, S.to);
      S.from.u = S.to.u; S.from.v = S.to.v;
      S.from.au = S.to.au; S.from.av = S.to.av; S.from.roll = S.to.roll;
      S.cu = S.to.u; S.cv = S.to.v;
      S.u = S.to.u; S.v = S.to.v;
      S.au = S.to.au; S.av = S.to.av; S.roll = S.to.roll;
    }

    // normalised screen offsets — what the BODY performs against
    const uN = clamp(S.u / GUARD.SPREAD_X, -1.3, 1.3);
    const vN = clamp(S.v / GUARD.SPREAD_Y, -1.6, 1.6);
    const w = treasure ? clamp(r, 0, 1.2) : 0;
    const rise = clamp(vN, -1, 1) * (vN >= 0 ? ARM.RISE_HIGH : -ARM.RISE_LOW);

    // ── the torso performs the angle ──────────────────────────
    // He STANDS UP into a high guard and DROPS into a low one, he
    // leans into the side it comes from, he squashes on contact,
    // and a MISS shoves him bodily backward. Before this pass a
    // high guard and a low guard moved exactly zero muscles.
    if (inner.current) {
      const bob = walking ? Math.sin(ph * 2) * 0.028 : 0;
      const breathe = walking ? 0 : Math.sin(t * 0.9) * 0.012;
      const sq = E.imp;
      inner.current.position.set(
        -uN * 0.035 * w - E.broke * uN * 0.07,
        bob + rise * w - E.broke * 0.05,
        -E.broke * 0.10
      );
      inner.current.rotation.set(
        (walking ? 0.06 : 0) + E.broke * 0.28 - vN * 0.05 * w,
        uN * ARM.TWIST * 0.4 * w,
        (walking ? Math.sin(ph) * 0.05 : 0) - uN * ARM.LEAN * w
      );
      inner.current.scale.set(
        1 + breathe + sq * 0.09,
        1 + breathe - sq * 0.11,
        1 + breathe + sq * 0.09
      );
    }

    // ── feet: he takes a stance ────────────────────────────────
    // FIXED: both feet used to be driven to z = ±0.07 around ZERO,
    // discarding the 0.04 the JSX places them at.
    const stance = uN * 0.05 * w;
    if (footL.current) {
      footL.current.position.set(-0.10 + stance - Math.max(0, -vN) * 0.03 * w,
                                 0.05, 0.04 + Math.sin(ph) * 0.07 * speed);
    }
    if (footR.current) {
      footR.current.position.set(0.10 + stance + Math.max(0, -vN) * 0.03 * w,
                                 0.05, 0.04 + Math.sin(ph + Math.PI) * 0.07 * speed);
    }

    // ── the free paw counterbalances ───────────────────────────
    // FIXED: this used to be driven to y = 0.02 + bob, throwing away
    // the 0.24 the JSX mounts it at — so the moment the first frame
    // ran, Perry's spare paw fell to his ankle and stayed there.
    // That is one of the loose blobs down by his feet.
    if (pawL.current) {
      pawL.current.position.set(
        -0.24 - uN * 0.05 * w,
        0.24 + Math.max(0, Math.sin(ph)) * 0.05 * speed - vN * 0.07 * w,
        0.10 + E.imp * 0.05
      );
    }

    // ══════════════════════════════════════════════════════════
    //  THE ARM — two bones, fixed lengths, a solved elbow
    // ══════════════════════════════════════════════════════════
    // REBUILT 2026-07-27. The old rig was a single cylinder whose
    // LENGTH was scaled 0.12 → past 1.0 every frame, anchored at
    // (p.x, 0.34, p.z) — Perry's own centreline. A rubber hose
    // growing out of his chest, with no shoulder anywhere in it.
    //
    // Now: a deltoid at a real shoulder, an upper arm and a forearm
    // that are ALWAYS their own length, and an elbow solved by the
    // cosine rule with the pole vector biased downward so it hangs
    // like an elbow instead of pointing at the lens. Out of reach,
    // the SHOULDER travels and the torso leans — which is what a
    // body does, and looks better than any amount of stretching.
    if (armRig.current && p) {
      const m = camera.matrixWorld.elements;
      const rX = m[0], rY = m[1], rZ = m[2];      // camera RIGHT
      const uX = m[4], uY = m[5], uZ = m[6];      // camera UP
      const py = p.y || 0;
      const cy = py + GUARD.CENTER_Y;             // the rose's centre

      S.sh.set(
        p.x + rX * ARM.SH_U + uX * ARM.SH_V,
        cy + rY * ARM.SH_U + uY * ARM.SH_V,
        p.z + rZ * ARM.SH_U + uZ * ARM.SH_V
      );
      S.hand.set(
        p.x + rX * S.u + uX * S.v,
        cy + rY * S.u + uY * S.v,
        p.z + rZ * S.u + uZ * S.v
      );
      // THE FLOOR. The bottom row now sits at world y 0.05 — the
      // dirt, deliberately, because that is where a low blow comes
      // from. But the swing OVERSHOOTS its target by design, and on
      // the bottom row the overshoot put the paw at y −0.05: buried.
      // Costs one comparison and it is the only thing standing
      // between a low parry and Perry punching through the road.
      if (S.hand.y < ARM.FLOOR_Y) S.hand.y = ARM.FLOOR_Y;

      // reach — and the lean that buys the last of it
      S.dir.subVectors(S.hand, S.sh);
      let reach = S.dir.length() || 1e-4;
      S.dir.multiplyScalar(1 / reach);
      // Both clamps MOVE something. Clamping the number alone was a
      // silent bone-length break: the elbow got solved for a length
      // the forearm then had to stretch to cover — the exact noodle
      // this rebuild exists to delete. Too far, the shoulder travels
      // (he leans into it). Too near, the paw is held off his chest.
      if (reach > ARM_MAX) {
        S.sh.addScaledVector(S.dir, reach - ARM_MAX);
        reach = ARM_MAX;
      } else if (reach < ARM_MIN) {
        S.hand.addScaledVector(S.dir, ARM_MIN - reach);
        reach = ARM_MIN;
      }

      // the elbow, by the cosine rule
      const a = (ARM.UPPER * ARM.UPPER - ARM.FORE * ARM.FORE + reach * reach) / (2 * reach);
      const hh = Math.sqrt(Math.max(0, ARM.UPPER * ARM.UPPER - a * a));
      S.fwd.set(-m[8], -m[9], -m[10]);
      S.pole.crossVectors(S.fwd, S.dir);
      if (S.pole.lengthSq() < 1e-6) S.pole.set(rX, rY, rZ);
      S.pole.normalize();
      if (S.pole.y > 0) S.pole.negate();          // an elbow hangs low
      S.elbow.copy(S.sh).addScaledVector(S.dir, a).addScaledVector(S.pole, hh);

      // bone 1 — shoulder → elbow
      if (upper.current) {
        S.tmp.subVectors(S.elbow, S.sh);
        const l1 = S.tmp.length() || 1e-4;
        S.tmp.multiplyScalar(1 / l1);
        S.qa.setFromUnitVectors(UP, S.tmp);
        upper.current.quaternion.copy(S.qa);
        upper.current.position.set(
          (S.sh.x + S.elbow.x) * 0.5 - p.x,
          (S.sh.y + S.elbow.y) * 0.5 - py,
          (S.sh.z + S.elbow.z) * 0.5 - p.z
        );
      }
      // bone 2 — elbow → paw
      if (fore.current) {
        S.tmp.subVectors(S.hand, S.elbow);
        const l2 = S.tmp.length() || 1e-4;
        S.tmp.multiplyScalar(1 / l2);
        S.qa.setFromUnitVectors(UP, S.tmp);
        fore.current.quaternion.copy(S.qa);
        fore.current.position.set(
          (S.elbow.x + S.hand.x) * 0.5 - p.x,
          (S.elbow.y + S.hand.y) * 0.5 - py,
          (S.elbow.z + S.hand.z) * 0.5 - p.z
        );
      }
      // the joints, so the limb reads as jointed
      if (deltoid.current) deltoid.current.position.set(S.sh.x - p.x, S.sh.y - py, S.sh.z - p.z);
      if (elbowBall.current) elbowBall.current.position.set(S.elbow.x - p.x, S.elbow.y - py, S.elbow.z - p.z);

      // the paw and what it is holding. The rig's local +Y IS the
      // direction the blade points, solved in guardPose to land on
      // the contact point from wherever the paw actually ended up;
      // the angle's authored `roll` then twists it on its own haft,
      // which is what roll always meant and now literally is.
      S.aim.set(
        rX * S.au + uX * S.av,
        rY * S.au + uY * S.av,
        rZ * S.au + uZ * S.av
      ).normalize();
      S.qa.setFromUnitVectors(UP, S.aim);
      S.qb.setFromAxisAngle(UP, S.roll);
      S.qa.multiply(S.qb);
      armRig.current.quaternion.copy(S.qa);
      armRig.current.position.set(S.hand.x - p.x, S.hand.y - py, S.hand.z - p.z);

      // ── the head watches the fire ────────────────────────────
      // Converted into the yaw group's own frame, so it is a real
      // head turn and not a screen-space cheat that breaks the
      // moment the camera cuts.
      if (head.current) {
        S.tmp.copy(S.hand).addScaledVector(S.aim, 0.40);
        const dx = S.tmp.x - p.x;
        const dy = S.tmp.y - (py + 0.62);
        const dz = S.tmp.z - p.z;
        const cs = Math.cos(yaw.current), sn = Math.sin(yaw.current);
        const hlx = dx * cs - dz * sn;
        const hlz = dx * sn + dz * cs;
        const flat = Math.hypot(hlx, hlz) || 1e-4;
        const wy = clamp(Math.atan2(hlx, hlz), -0.6, 0.6) * ARM.LOOK * (treasure ? 1 : 0);
        const wx = clamp(-Math.atan2(dy, flat), -0.5, 0.42) * ARM.LOOK * (treasure ? 1 : 0);
        const kk = Math.min(1, dt * 14);
        head.current.rotation.y += (wy - head.current.rotation.y) * kk;
        head.current.rotation.x += (wx - head.current.rotation.x) * kk;
        head.current.rotation.z = -E.ring * 0.10 + E.broke * 0.18;
      }
    }

    // ── contact reads on the whole body ───────────────────────
    // The rim aura is already a full-body shell; on an answer it
    // becomes the flash. White-hot for a PERFECT, red for a guard
    // that got driven through. Zero extra lights, zero new meshes.
    if (aura.current?.material) {
      const g0 = gd?.grade;
      const flash = E.imp * (g0 === "PERFECT" || g0 === "FRACTURE" ? 1 : 0.45);
      aura.current.material.opacity = 0.07 + flash * 0.5 + E.broke * 0.22;
      aura.current.material.color.set(
        E.broke > 0.05 ? "#ff6a5a" : flash > 0.2 ? "#fff2d8" : sp.glow
      );
    }
    // the spirit ember flares on an answer — the parry lights him up
    if (ember.current) ember.current.intensity = 2.6 + E.imp * 5.4;

    // tail sway — two sines, different rates (fox especially)
    if (tail.current) tail.current.rotation.y = Math.sin(t * 1.6) * 0.28 + Math.sin(t * 0.7) * 0.14;
    // wisp orbit, fixed phases
    wisps.current.forEach((w, i) => {
      if (!w) return;
      const a = t * (0.7 + i * 0.23) + i * 2.1;
      w.position.set(Math.cos(a) * 0.42, 0.34 + Math.sin(a * 1.7) * 0.12, Math.sin(a) * 0.42);
      const m = w.material;
      if (m) m.opacity = 0.35 + Math.sin(t * 2 + i) * 0.2;
    });
  });

  return (
    <group ref={rigRoot}>
      <group ref={group}>
      <group ref={inner}>
        {/* ── torso — a loaf, not a crate ── */}
        <group position={[0, 0.28, 0]}>
          <mesh castShadow scale={[0.85, 0.72, 1.05]}>
            <sphereGeometry args={[0.26, 16, 14]} />
            <meshStandardMaterial map={coat} roughness={0.88} emissive={sp.glow} emissiveIntensity={0.05} />
          </mesh>
          <mesh scale={[0.85 * 1.05, 0.72 * 1.05, 1.05 * 1.05]}>
            <sphereGeometry args={[0.26, 16, 14]} />
            <meshBasicMaterial color={INK} side={THREE.BackSide} />
          </mesh>
          {/* chest floof */}
          <mesh position={[0, -0.02, 0.2]} scale={[0.6, 0.5, 0.4]}>
            <sphereGeometry args={[0.2, 12, 10]} />
            <meshStandardMaterial color={sp.coatHot} roughness={0.95} />
          </mesh>
        </group>

        {/* ── head — the beb is mostly head ── */}
        <group ref={head} position={[0, 0.62, 0.06]}>
          <mesh castShadow scale={[1.0, 0.92, 0.95]}>
            <sphereGeometry args={[0.24, 18, 16]} />
            <meshStandardMaterial map={coat} roughness={0.85} emissive={sp.glow} emissiveIntensity={0.05} />
          </mesh>
          <mesh scale={[1.05, 0.92 * 1.05, 0.95 * 1.05]}>
            <sphereGeometry args={[0.24, 18, 16]} />
            <meshBasicMaterial color={INK} side={THREE.BackSide} />
          </mesh>
          {/* cheek fluff */}
          {[-0.18, 0.18].map((x, i) => (
            <mesh key={i} position={[x, -0.06, 0.06]} scale={[0.5, 0.4, 0.45]}>
              <sphereGeometry args={[0.14, 10, 8]} />
              <meshStandardMaterial color={sp.coat} roughness={0.95} />
            </mesh>
          ))}
          {/* eyes — painted lamps in dark sockets, angled on the skull */}
          {[-0.105, 0.105].map((x, i) => (
            <group key={`e${i}`} position={[x, 0.03, 0.185]} rotation={[0, x > 0 ? 0.34 : -0.34, 0]}>
              <mesh position={[0, 0, -0.012]} scale={[1.15, 1.3, 0.6]}>
                <sphereGeometry args={[0.062, 10, 10]} />
                <meshStandardMaterial color="#0a0604" roughness={0.4} />
              </mesh>
              <mesh position={[0, 0, 0.028]}>
                <circleGeometry args={[0.055, 20]} />
                <meshBasicMaterial map={eye} toneMapped={false} />
              </mesh>
            </group>
          ))}
          <SpeciesDetails sp={sp} coat={coat} />
        </group>

        {/* ── mitten paws with pale socks ──
            THE THIRD PAW, killed 2026-07-27. Whenever a Treasure
            was equipped this rendered pawL, pawR AND the arm rig's
            own paw — three hands on a two-handed creature. The
            armed paw lives on the rig now, so pawR only exists
            when there is nothing in it. */}
        <group ref={pawL} position={[-0.24, 0.24, 0.1]}>
          <mesh castShadow>
            <sphereGeometry args={[0.075, 10, 10]} />
            <meshStandardMaterial color={sp.coatHot} roughness={0.9} />
          </mesh>
        </group>
        {!treasure && (
          <group ref={pawR} position={[0.24, 0.24, 0.1]}>
            <mesh castShadow>
              <sphereGeometry args={[0.075, 10, 10]} />
              <meshStandardMaterial color={sp.coatHot} roughness={0.9} />
            </mesh>
          </group>
        )}


        {/* ── feet ── */}
        <group ref={footL} position={[-0.1, 0.05, 0.04]}>
          <mesh castShadow scale={[1, 0.7, 1.3]}>
            <sphereGeometry args={[0.08, 10, 8]} />
            <meshStandardMaterial map={coat} roughness={0.9} />
          </mesh>
        </group>
        <group ref={footR} position={[0.1, 0.05, 0.04]}>
          <mesh castShadow scale={[1, 0.7, 1.3]}>
            <sphereGeometry args={[0.08, 10, 8]} />
            <meshStandardMaterial map={coat} roughness={0.9} />
          </mesh>
        </group>

        {/* ── tail — the house bead-chain, glowing tip ── */}
        <group ref={tail} position={[0, 0.24, -0.24]}>
          {[0, 1, 2, 3, 4].map((i) => {
            const r = 0.075 - i * 0.011;
            const isFox = sp.id === "shadow_fox";
            return (
              <mesh
                key={i}
                position={[0, i * 0.055 * (isFox ? 1.25 : 0.8), -i * 0.055]}
                castShadow={i < 2}
              >
                <sphereGeometry args={[Math.max(0.025, r), 10, 8]} />
                {i === 4 ? (
                  <meshBasicMaterial color={sp.glow} toneMapped={false} />
                ) : (
                  <meshStandardMaterial map={coat} roughness={0.9} />
                )}
              </mesh>
            );
          })}
        </group>

        {/* ── rim aura — the magic, zero lights, and now the
            contact flash as well (see the frame loop) ── */}
        <mesh ref={aura} position={[0, 0.42, 0]} scale={[1.5, 1.7, 1.5]}>
          <sphereGeometry args={[0.3, 14, 12]} />
          <meshBasicMaterial
            color={sp.glow} transparent opacity={0.07}
            side={THREE.BackSide} depthWrite={false} toneMapped={false}
          />
        </mesh>

        {/* ── orbiting wisps ── */}
        {[0, 1, 2].map((i) => (
          <mesh key={`wi${i}`} ref={(el) => { wisps.current[i] = el; }}>
            <sphereGeometry args={[0.02, 8, 8]} />
            <meshBasicMaterial color={sp.glow} transparent opacity={0.4} toneMapped={false} depthWrite={false} />
          </mesh>
        ))}
      </group>

      {/* the spirit ember — the light that walks with him */}
      {emberLight && (
        <pointLight
          ref={ember}
          position={[0.2, 0.9, 0.25]}
          intensity={2.6}
          distance={7.5}
          decay={1.35}
          color={sp.glow}
          castShadow={false}
        />
      )}
      </group>

      {/* ══════════════════════════════════════════════════════
          THE ARM RIG — deliberately NOT inside <group ref={inner}>
          and NOT subject to Perry's yaw. The guard is a fact about
          the SCREEN (see the guard block above and GUARD's Screen
          Law), so the thing that performs it may not be rotated by
          which way a beb happens to be facing. He is a sphere with
          a face; nobody will ever notice, and it is the difference
          between a readable parry and a floating bead of light.

          REBUILT 2026-07-27 as a real limb. Every mesh here is
          placed imperatively from the frame loop, and — this is the
          whole point — NOTHING IS EVER SCALED. Both bones carry
          their length in the geometry. An arm that changes length
          is a noodle, and a noodle is what we had.
          ══════════════════════════════════════════════════════ */}
      {treasure && (
        <>
          {/* the shoulder. He has one now. */}
          <mesh ref={deltoid} castShadow scale={[1.15, 1, 1]}>
            <sphereGeometry args={[0.068, 10, 10]} />
            <meshStandardMaterial map={coat} roughness={0.9} />
            <mesh scale={1.1}>
              <sphereGeometry args={[0.068, 10, 10]} />
              <meshBasicMaterial color={INK} side={THREE.BackSide} />
            </mesh>
          </mesh>

          {/* upper arm — shoulder to elbow, fixed.
              The ink shell is a CHILD, not a sibling: two meshes
              cannot share one ref, the second would simply null the
              first and the arm would vanish. */}
          <mesh ref={upper} castShadow>
            <cylinderGeometry args={[0.040, 0.052, ARM.UPPER, 8]} />
            <meshStandardMaterial map={coat} roughness={0.9} />
            <mesh scale={[1.16, 1.02, 1.16]}>
              <cylinderGeometry args={[0.040, 0.052, ARM.UPPER, 8]} />
              <meshBasicMaterial color={INK} side={THREE.BackSide} />
            </mesh>
          </mesh>

          {/* the elbow */}
          <mesh ref={elbowBall} castShadow>
            <sphereGeometry args={[0.043, 8, 8]} />
            <meshStandardMaterial color={sp.coat} roughness={0.92} />
            <mesh scale={1.14}>
              <sphereGeometry args={[0.043, 8, 8]} />
              <meshBasicMaterial color={INK} side={THREE.BackSide} />
            </mesh>
          </mesh>

          {/* forearm — elbow to paw, fixed */}
          <mesh ref={fore} castShadow>
            <cylinderGeometry args={[0.034, 0.043, ARM.FORE, 8]} />
            <meshStandardMaterial map={coat} roughness={0.92} />
            <mesh scale={[1.18, 1.02, 1.18]}>
              <cylinderGeometry args={[0.034, 0.043, ARM.FORE, 8]} />
              <meshBasicMaterial color={INK} side={THREE.BackSide} />
            </mesh>
          </mesh>

          {/* the paw, and what it is holding. The rig is AIMED —
              its local +Y is the direction the Treasure points, so
              the blade genuinely faces the fire coming at it. */}
          <group ref={armRig}>
            <mesh castShadow>
              <sphereGeometry args={[0.082, 10, 10]} />
              <meshStandardMaterial color={sp.coatHot} roughness={0.9} />
            </mesh>
            <mesh scale={1.1}>
              <sphereGeometry args={[0.082, 10, 10]} />
              <meshBasicMaterial color={INK} side={THREE.BackSide} />
            </mesh>
            {/* dropped so the PAW sits on the grip, not on the butt
                of the haft — the standoff maths in guardPose assumes
                exactly this offset. */}
            <group position={[0, -gripDrop, 0]}>
              <TreasureBody treasure={treasure} finish={finish} guardRef={guardRef} scale={ARM.TR_SCALE} />
            </group>
          </group>
        </>
      )}
    </group>
  );
}
